import {
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  InfoOutlined,
  NotificationsNoneOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { Badge, Box, Button, Divider, IconButton, List, Menu, MenuItem, Typography } from '@mui/material';
import React, { useState } from 'react';

import { muiPalette } from '@/shared/general-theme';

interface Notification {
  id: number;
  message: string;
  type: 'error' | 'info' | 'warning' | 'success';
  isRead: boolean;
}

const NotificationCenter: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([
    { id: 1, message: 'An error occurred', type: 'error', isRead: false },
    { id: 2, message: 'Your report is ready', type: 'info', isRead: false },
    { id: 3, message: 'Disk space is low', type: 'warning', isRead: false },
    { id: 4, message: 'Operation successful', type: 'success', isRead: false },
  ]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (id: number) => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((n) =>
        n.id === id ? { ...n, isRead: true } : n,
      ),
    );
  };

  const markAllAsRead = () => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((n) => (
        { ...n, isRead: true }
      )),
    );
  };

  const getNotifIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'warning':
        return <WarningAmberOutlined sx={{ color: 'warning.main', fontSize: 20 }} />;
      case 'error':
        return <ErrorOutlineOutlined sx={{ color: 'error.main', fontSize: 20 }} />;
      default:
        return <InfoOutlined sx={{ color: 'primary.main', fontSize: 20 }} />;
    }
  };

  return (
    <>
      <IconButton onClick={handleOpen} aria-label='notifications'>
        <Badge badgeContent={unreadCount} color='error'>
          <NotificationsNoneOutlined />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '90vw', sm: 360 },
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
            },
          },
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          p={2}
        >
          <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>Notifications</Typography>
          <Button
            variant='text'
            size='medium'
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            sx={{ p: 'initial' }}
          >
            Mark all as read
          </Button>
        </Box>

        <Divider />

        {notifications.length === 0 ? (
          <MenuItem
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',

              ':hover': {
                backgroundColor: 'transparent',
                cursor: 'default',
              },
            }}
          >
            <Typography align='center' color='textSecondary'>
              No notifications
            </Typography>
          </MenuItem>
        ) : (
          [
            <List key={'list'} sx={{ py: 0, maxHeight: 400, overflow: 'auto' }}>
              {notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onMouseEnter={() => handleMarkAsRead(notification.id)}
                  sx={{
                    py: 2,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: muiPalette.grey[100],
                    whiteSpace: 'normal',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ mr: 2, mt: 0.5 }}>{getNotifIcon(notification.type)}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant='body2'
                      sx={{ fontWeight: notification.isRead ? 400 : 600, color: muiPalette.text.primary, mb: 0.5 }}
                    >
                      {notification.message}
                    </Typography>
                  </Box>
                  {!notification.isRead &&
                   <Box
                     sx={{
                       width: 6,
                       height: 6,
                       borderRadius: '50%',
                       bgcolor: muiPalette.primary.main,
                     }}
                   />}
                </MenuItem>
              ))}
            </List>,
            <Box key={'box'} sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Button fullWidth size='small' sx={{ fontWeight: 600 }}>View All Activity (ToDo)</Button>
            </Box>,
          ]
        )}
      </Menu>
    </>
  );
};

export default NotificationCenter;